import {
  BackstageIdentityApi,
  BackstageIdentityResponse,
  DiscoveryApi,
  ProfileInfo,
  ProfileInfoApi,
} from '@backstage/core-plugin-api';

/**
 * Auth API mínima usada por el proveedor "Guest (full access)" de la
 * pantalla de login, pensado ÚNICAMENTE para depurar en local (ver
 * `packages/backend/src/authGuestFullProvider.ts`). Inicia sesión contra el
 * segundo proveedor "guest" del backend (`guest-full`), que la política de
 * permisos no reconoce como invitado ni externo, por lo que obtiene acceso
 * completo, sin las restricciones del guest habitual.
 *
 * Se registra como un `apiRef` más (ver `apis.ts`) para que Backstage lo
 * renderice como una tarjeta estándar dentro de la misma rejilla de
 * proveedores de login, justo al lado de "Guest".
 */
export class GuestFullAuthApi implements ProfileInfoApi, BackstageIdentityApi {
  private readonly discoveryApi: DiscoveryApi;
  private sessionPromise?: Promise<{
    profile: ProfileInfo;
    backstageIdentity: BackstageIdentityResponse;
  }>;

  constructor(discoveryApi: DiscoveryApi) {
    this.discoveryApi = discoveryApi;
  }

  private async getSession() {
    if (!this.sessionPromise) {
      this.sessionPromise = this.fetchSession();
    }
    return this.sessionPromise;
  }

  private async fetchSession() {
    const baseUrl = await this.discoveryApi.getBaseUrl('auth');
    const response = await fetch(`${baseUrl}/guest-full/refresh`, {
      credentials: 'include',
      headers: { 'X-Requested-With': 'XMLHttpRequest' },
    });
    if (!response.ok) {
      throw new Error(
        `Failed to sign in as guest-full: ${response.status} ${response.statusText}`,
      );
    }
    return response.json();
  }

  async getBackstageIdentity(): Promise<BackstageIdentityResponse | undefined> {
    const session = await this.getSession();
    return session.backstageIdentity;
  }

  async getProfile(): Promise<ProfileInfo | undefined> {
    const session = await this.getSession();
    return session.profile;
  }

  async signOut(): Promise<void> {
    this.sessionPromise = undefined;
  }
}
